<template>
  <VCard
    v-if="isExpanded && currentTeam && me?.org"
    ref="teamSwitcherCardRef"
    border
    class="pa-3 mx-4 mt-4 d-flex flex-row align-center ga-2 cursor-pointer truncate"
    width="238"
    data-testid="teamSwitcherCard"
    role="button"
    tabindex="0"
    aria-haspopup="menu"
    :aria-expanded="isOpen"
    :aria-label="t('switchTeam')"
    @click="toggleMenu"
    @keydown.enter="toggleMenu"
    @keydown.space.prevent="toggleMenu"
    @keydown.escape.stop="closeMenu"
  >
    <CommonAvatar
      data-testid="teamSwitcherAvatarExpanded"
      size="40"
      :user-name="currentTeam?.name"
      :use-initials="true"
    />
    <div class="d-flex flex-column min-w-0 text-truncate prevent-select-text">
      <div
        class="text-body-2 font-weight-bold text-truncate"
        data-testid="elementTeamName"
      >
        {{ currentTeam.name }}
      </div>
      <div
        class="text-body-2 font-wight-semibold text-medium-emphasis text-truncate"
        data-testid="elementOrganizationName"
      >
        {{ me?.org.name }}
      </div>
    </div>
    <VIcon
      size="16"
      icon="swap_horiz"
    />
  </VCard>
  <CommonAvatar
    v-else-if="currentTeam"
    class="mx-5"
    data-testid="teamSwitcherAvatarCollapsed"
    size="40"
    :user-name="currentTeam?.name"
    :use-initials="true"
  />
  <VMenu
    v-if="isExpanded && currentTeam && me?.org"
    :model-value="isOpen"
    :activator="teamSwitcherCardRef!"
    location="bottom center"
    origin="top center"
    theme="TwLight"
    :min-width="238"
    :width="238"
    @update:model-value="clearSearchOnClose"
  >
    <VCard
      v-click-outside="toggleMenu"
      border
      class="pa-3"
      data-testid="teamSwitcherDropdownMenu"
      @keydown.escape.stop="closeMenu"
    >
      <div
        class="d-flex align-center justify-space-between mb-2"
      >
        <div class="text-body-2 font-weight-semibold team-switcher__title">
          {{ t('team', 2) }}
        </div>
        <!-- GM users currently only have one org
         <div
          class="d-flex align-center text-body-2 font-weight-semibold
          cursor-pointer org-switcher__btn"
        >
          <span>{{ t('switchOrgs') }}</span>
          <VIcon
            class="ml-1"
            size="16"
            icon="swap_horiz"
          />
        </div> -->
      </div>
      <CommonClearableSearchField
        v-model="searchQuery"
        :label="t('search')"
        :placeholder="t('search')"
        hide-details
        density="compact"
      />
      <div class="team-switcher-list">
        <VList>
          <VListItem
            v-for="team in filteredTeams"
            :key="team.twrn"
            class="pa-2"
            theme="TwLight"
            :rounded="true"
            height="32"
            @click="switchTeam(team.twrn)"
          >
            <template #prepend>
              <CommonAvatar
                :use-initials="true"
                :user-name="team.name"
                size="32"
              />
            </template>
            <VListItemTitle
              class="select-none"
              data-testid="elementTeamNameInDropdown"
            >
              {{ team.name }}
            </VListItemTitle>
            <template
              v-if="team.twrn === currentTeam?.twrn"
              #append
            >
              <VIcon
                size="16"
                icon="check"
              />
            </template>
          </VListItem>
        </VList>
      </div>
    </VCard>
  </VMenu>
</template>

<script lang="ts" setup>
import { computed, watch, ref } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';
import { CommonAvatar } from '@teamworksdev/vuetify';
import CommonClearableSearchField from '@/components/common/CommonClearableSearchField.vue';
import { useSearch } from '@/composables/useSearch';
import { useMeQuery } from '@/composables/useMeQuery';
import { useMainStore } from '@/stores/mainStore';
import { useAuth } from '@/composables/useAuth';
import { useRouter, useRoute } from 'vue-router';

const mainStore = useMainStore();
const auth = useAuth();
const router = useRouter();
const route = useRoute();
const queryClient = useQueryClient();
const { data: me, error } = useMeQuery();

watch(error, async () => {
  if (error.value) {
    // Check if this is a 401 (unauthorized) - should show error boundary
    const isUnauthorized = error.value && typeof error.value === 'object' && 'response' in error.value
      && (error.value as any).response?.status === 401;

    // Only logout and throw for 401 errors (authentication failures)
    // Other errors (like network errors) are just temporary and shouldn't log you out
    if (isUnauthorized) {
      await auth.logOut();
      throw error.value;
    }
  }
});

const teamSwitcherCardRef = ref<HTMLElement | null>(null);

const { isExpanded = false } = defineProps<{
  isExpanded: boolean;
}>();

const isOpen = ref(false);

const { searchQuery, reset: resetSearch } = useSearch();
const { t } = useI18n();

const currentTeam = computed(() => {
  if (me?.value && mainStore.teamTwrn) {
    return me.value.teams.find((team) => team.twrn === mainStore.teamTwrn);
  }
  return null;
});

const filteredTeams = computed(() => {
  return searchQuery.value ? me?.value?.teams.filter((team) => team.name.toLowerCase().includes(searchQuery.value.toLowerCase())) : me?.value?.teams;
});

const clearSearchOnClose = (isOpen: boolean) => {
  if (!isOpen) {
    resetSearch();
  }
};

const switchTeam = async (twrn: string) => {
  isOpen.value = false;
  if (twrn === currentTeam.value?.twrn) return;

  // Check if we're on the org budgets page (TeamBudgets route now directly loads OrgBudgets)
  const isOnOrgBudgets = route.name === 'TeamBudgets';

  if (isOnOrgBudgets) {
    // If on org budgets page, update team and apply the team filter
    mainStore.setTeamTwrn(twrn);

    // Navigate to the same route with team filter applied
    await router.replace({
      name: 'TeamBudgets',
      query: { teams: twrn },
    });

    await queryClient.invalidateQueries();
  } else {
    // For other pages, navigate to TeamBudgets with team filter applied
    mainStore.setTeamTwrn(twrn);
    await router.push({
      name: 'TeamBudgets',
      query: { teams: twrn },
    });

    await queryClient.invalidateQueries();
  }
};

const closeMenu = () => {
  isOpen.value = false;
};
const toggleMenu = () => {
  isOpen.value = !isOpen.value;
};

</script>

<style lang="scss" scoped>
.org-switcher {
  &__btn {
    color: rgb(var(--v-theme-info));
    line-height: 20px;
  }
}
.team-switcher{
  &__title{
    line-height: 20px;
  }
}
.team-switcher-list{
  max-height: 300px;
  overflow-y: auto;
}
</style>
